import * as msal from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'
import { useEffect, useState } from 'react'
import { envConfig } from '../config'

let clientInstance: undefined | msal.PublicClientApplication = undefined

export const loginConfig = {
  // we have to have at least ONE scope as per this post
  // https://tonesandtones.github.io/azure-ad-and-the-unvalidatable-access-token/
  // this cost me hours....
  scopes: [`api://${envConfig().msal.clientId}/Payments.Read`],
  redirectUri: window.location.origin
}

export const msalClient = () => {
  if (clientInstance) {
    return clientInstance
  }
  const { msal: msalConfig } = envConfig()
  clientInstance = new msal.PublicClientApplication({
    auth: {
      clientId: msalConfig.clientId,
      postLogoutRedirectUri: window.location.origin,
      authority: `https://login.microsoftonline.com/${msalConfig.tenant}`
    }
  })
  return clientInstance
}

/**
 * `useToken` is called ~86 times across the admin UI (usePayment alone has ~11),
 * and every call site runs its own effect against the SAME singleton client.
 * On a route like /admin/payments they mount in the same tick, all observe
 * InteractionStatus.None, and all start an acquisition — the first interactive
 * one wins and the rest throw `interaction_in_progress`.
 *
 * Deduping here rather than guarding per call site: one in-flight promise all
 * callers await, so N components produce one acquisition. Keyed by account id
 * so switching accounts doesn't hand back the previous account's token.
 */
let inFlight: { key: string; promise: Promise<string> } | null = null

function acquireToken(
  client: msal.PublicClientApplication,
  account: msal.AccountInfo
): Promise<string> {
  const key = account.homeAccountId

  if (inFlight?.key === key) {
    return inFlight.promise
  }

  const request = {
    ...loginConfig,
    cacheLookupPolicy: msal.CacheLookupPolicy.Default,
    account
  }

  const promise = (async () => {
    try {
      const result = await client.acquireTokenSilent(request)

      return result.accessToken
    } catch (e) {
      // Interactive fallbacks are the ones that collide, so they run inside the
      // shared promise too. acquireTokenRedirect navigates away and never
      // resolves; the popup path returns a token for everyone awaiting.
      if (e instanceof msal.InteractionRequiredAuthError) {
        await client.acquireTokenRedirect(request)

        return ''
      }

      const result = await client.acquireTokenPopup(request)

      return result.accessToken
    }
  })()

  inFlight = { key, promise }

  // Let the next mount retry rather than caching a rejection forever.
  promise.catch(() => undefined).finally(() => {
    if (inFlight?.promise === promise) {
      inFlight = null
    }
  })

  return promise
}

export const useToken = () => {
  const [token, setToken] = useState('')
  const { accounts, inProgress } = useMsal()

  useEffect(() => {
    let isActive = true

    async function getToken() {
      const account = accounts?.[0]

      if (!account) {
        if (isActive) {
          setToken('')
        }

        return
      }

      if (inProgress !== msal.InteractionStatus.None) {
        return
      }

      try {
        const accessToken = await acquireToken(msalClient(), account)

        if (isActive && accessToken) {
          setToken(accessToken)
        }
      } catch {
        // Acquisition failed for every awaiting caller; MsalAuthenticationTemplate
        // drives re-auth. Swallowing here keeps one failure from becoming N
        // unhandled rejections across the mounted call sites.
      }
    }

    getToken()

    return () => {
      isActive = false
    }
  }, [accounts, inProgress])

  return token
}
